"use client";

import { LockIcon, RotateCcwIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Button } from "../button";

export interface InheritableFieldSlot {
	/** Pass to the wrapped FieldType's `labelTrailing`. */
	labelTrailing: ReactNode;
	/** Bulk scope with differing child values: render the field's Mixed placeholder. */
	mixed: boolean;
	/** The org locks this value (R100): render the field disabled. */
	locked: boolean;
}

export interface InheritableFieldProps {
	/**
	 * Where the value comes from, already translated: "From Module · Negotiation basics",
	 * "Org default", "Set by org", "From your defaults", "Set here".
	 */
	originLabel: string;
	/** True when this scope sets the value itself; only then can it be reset. */
	overridden: boolean;
	/** Remove this scope's value so it inherits again. */
	onReset?: () => void;
	/** Translated accessible label for the reset button, e.g. "Reset to inherited". */
	resetLabel: string;
	/** Bulk scopes (`scope-setting-uniformity`): children disagree. */
	mixed?: boolean;
	/** Translated chip text for the mixed state, e.g. "Mixed". */
	mixedLabel?: string;
	disabled?: boolean;
	/** The org locks this key (policy LOCK): no override, no reset, the chip shows a lock. */
	locked?: boolean;
	children: (slot: InheritableFieldSlot) => ReactNode;
}

/**
 * Wraps any FieldType with the settings-cascade chrome (create-orchestration-governance R9):
 * an origin chip, a reset-to-inherited button when this scope overrides, and the Mixed state
 * for bulk scopes. The FieldType renders the value; this adds where it came from.
 */
export function InheritableField({
	originLabel,
	overridden,
	onReset,
	resetLabel,
	mixed = false,
	mixedLabel,
	disabled,
	locked = false,
	children,
}: InheritableFieldProps) {
	const chipText = mixed && mixedLabel ? mixedLabel : originLabel;
	const labelTrailing = (
		<span className="flex items-center gap-1">
			<span
				data-testid="inheritable-field-origin"
				className={cn(
					"inline-flex max-w-[16rem] items-center truncate rounded-full px-2 py-0.5 text-xs",
					overridden && !mixed
						? "bg-primary/10 text-primary"
						: "bg-muted text-muted-foreground",
				)}
				title={chipText}
			>
				{locked ? (
					<LockIcon className="mr-1 size-3 shrink-0" aria-hidden />
				) : null}
				{chipText}
			</span>
			{overridden && onReset && !locked ? (
				<Button
					type="button"
					variant="ghost"
					size="icon"
					className="size-6"
					onClick={onReset}
					disabled={disabled}
					aria-label={resetLabel}
					title={resetLabel}
					data-testid="inheritable-field-reset"
				>
					<RotateCcwIcon className="size-3.5" aria-hidden />
				</Button>
			) : null}
		</span>
	);
	return <>{children({ labelTrailing, mixed, locked })}</>;
}
